import { useMemo } from "react"
import { getChartColor, getFileExtension } from "@pma/utils/helpers"
import { AnalyticsFilters } from "@pma/types"

export function useFileComputed({
  filesRaw,
  usedSpaceRaw,
  projectsRaw,
  filters,
}: {
  filesRaw: any[] | undefined
  usedSpaceRaw: any
  projectsRaw: any[] | undefined
  filters?: AnalyticsFilters
}) {
  const projectNameMap = useMemo(() => {
    const map: Record<number, string> = {}
    ;(projectsRaw || []).forEach((p: any) => {
      map[p.id] = p.title || p.name || `Project ${p.id}`
    })
    return map
  }, [projectsRaw])

  const files = useMemo(() => {
    let f = (filesRaw || []).filter((file: any) => !file.deleted)
    if (filters?.projectIds?.length) {
      f = f.filter((file: any) => filters.projectIds.includes(file.project_id))
    }
    if (filters?.dateRange?.from) {
      const from = filters.dateRange.from
      f = f.filter((file: any) => new Date(file.created_at || 0) >= from)
    }
    if (filters?.dateRange?.to) {
      const to = filters.dateRange.to
      f = f.filter((file: any) => new Date(file.created_at || 0) <= to)
    }
    if (filters?.userIds?.length) {
      f = f.filter((file: any) => {
        const createdById =
          file?._created_by?.id || file?.created_by_id || file?.created_by
        return createdById && filters.userIds.includes(createdById)
      })
    }
    return f
  }, [filesRaw, filters])

  const totalFiles = files.length

  const totalSizeBytes = useMemo(() => {
    if (usedSpaceRaw?.used_space != null) return Number(usedSpaceRaw.used_space)
    return files.reduce((sum: number, f: any) => sum + (Number(f.size) || 0), 0)
  }, [usedSpaceRaw, files])

  const filesThisMonth = useMemo(() => {
    const now = new Date()
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
    return files.filter((f: any) => new Date(f.created_at || 0) >= monthStart).length
  }, [files])

  const fileTypeDonut = useMemo(() => {
    const counts: Record<string, number> = {}
    files.forEach((f: any) => {
      const ext = getFileExtension(f.name || f.type || "unknown") || "other"
      counts[ext] = (counts[ext] || 0) + 1
    })
    return Object.entries(counts)
      .map(([ext, count], i) => ({
        title: ext.toUpperCase(),
        count,
        fill: getChartColor(i),
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [files])

  const storageByProject = useMemo(() => {
    const map: Record<number, number> = {}
    files.forEach((f: any) => {
      const pid = f.project_id
      if (pid) map[pid] = (map[pid] || 0) + (Number(f.size) || 0)
    })
    return Object.entries(map)
      .map(([pid, bytes], i) => ({
        projectId: Number(pid),
        name: projectNameMap[Number(pid)] || `Project ${pid}`,
        value: Math.round((bytes / 1_048_576) * 10) / 10,
        fill: getChartColor(i),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10)
  }, [files, projectNameMap])

  const uploadsByUser = useMemo(() => {
    const counts: Record<string, number> = {}
    files.forEach((f: any) => {
      const user = f._created_by?.name || f.created_by_name || "Unknown"
      counts[user] = (counts[user] || 0) + 1
    })
    return Object.entries(counts)
      .map(([name, count], i) => ({
        name,
        count,
        fill: getChartColor(i),
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [files])

  return {
    files,
    projectNameMap,
    totalFiles,
    totalSizeBytes,
    filesThisMonth,
    fileTypeDonut,
    storageByProject,
    uploadsByUser,
  }
}
